import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Container,
  Heading,
  SimpleGrid,
  Text,
} from "@chakra-ui/react";
import React from "react";
import { useQuery } from "react-query";
import { Link, Navigate, useParams } from "react-router-dom";
import { usePremium } from "../../../contexts/Premium";
import { trackEvent } from "../../../shared/analytics";
import Spinner from "../../../shared/components/Spinner";
import ReadingOrderCard from "../components/ReadingOrderCard";
import {
  discoverQueryKey,
  discoverQueryOptions,
  getDiscoverItems,
  getDiscoverSection,
} from "../helpers/discoverSections";

/**
 * "View all" page for a discovery row: the full list of (up to) 25 orders.
 * Mirrors the mobile DiscoverOrdersScreen.
 */
const DiscoverOrders = () => {
  const { section: sectionKey } = useParams();
  const section = getDiscoverSection(sectionKey);
  const { isPremium } = usePremium();

  const query = useQuery(
    discoverQueryKey(section?.key),
    () => section.fetch(),
    { ...discoverQueryOptions, enabled: !!section }
  );
  const items = React.useMemo(() => getDiscoverItems(query.data), [query.data]);

  if (!section) {
    return <Navigate to="/404" replace />;
  }

  let content;
  if (query.isLoading) {
    content = <Spinner />;
  } else if (query.isError) {
    content = (
      <Text>Oops, an error occurred: {query.error?.message || "."}</Text>
    );
  } else if (!items.length) {
    content = <Text>No entries to show.</Text>;
  } else {
    content = (
      <SimpleGrid columns={[1, 2, 2, 3]} spacing="16px">
        {items.map((readingOrder, index) => (
          <ReadingOrderCard
            key={`${readingOrder.id ?? "order"}-${index}`}
            id={readingOrder.id}
            title={readingOrder.title}
            thumbnail={readingOrder.thumbnail}
            comicsCount={readingOrder.comicsCount}
            readersCount={
              readingOrder.readersCount ?? readingOrder.readingCount
            }
            years={readingOrder.years}
            author={readingOrder.author}
            premiumOnly={Boolean(
              readingOrder.paywallPremiumOnly ?? readingOrder.premiumOnly
            )}
            isPremium={isPremium}
            hideStartAction
            onClick={() =>
              trackEvent("browse_discover_card_click", {
                section: section.key,
                readingOrderId: readingOrder.id,
                position: index + 1,
                source: "view_all",
              })
            }
          />
        ))}
      </SimpleGrid>
    );
  }

  return (
    <Container maxW="container.lg">
      <Breadcrumb mt="6">
        <BreadcrumbItem>
          <BreadcrumbLink as={Link} to="/browse">
            Browse
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem isCurrentPage>
          <BreadcrumbLink as={Link} to="#">
            {section.title}
          </BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>
      <Heading as="h2" mb="2">
        {section.title}
      </Heading>
      <Text color="gray.400" fontSize="sm" mb="6">
        {section.description}
      </Text>
      {content}
    </Container>
  );
};

export default DiscoverOrders;
